/**
 * Environment-varying configuration supplied at *serve* time (not baked into
 * the bundle), so one artifact is promoted across dev → alpha1 → alpha2.
 * Defaults live in `public/config.js`; deploying infra overwrites that file
 * per environment.
 */
export interface AppConfig {
  API_SERVER?: string;
  WEB_SERVER?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

/**
 * Reads the runtime config injected by public/config.js; `{}` when absent.
 *
 * Ignored in dev (`npm run dev`) and unit tests so local `.env` values keep
 * driving the app — public/config.js is served in dev too, so without this
 * gate its dev-portal defaults would override the developer's `.env`
 * (`VITE_LOCAL_API_SERVER_IP`, `VITE_WEB_SERVER=https://localhost:3000`) and
 * break the local login redirect and the pre-push Playwright suite. Every
 * *deployed* environment is a production build (`import.meta.env.PROD`), so the
 * runtime config only takes effect where it should — the "build once, deploy
 * many" contract.
 */
export function getAppConfig(): AppConfig {
  if (!import.meta.env.PROD) return {};
  if (typeof window === 'undefined') return {};
  // oxlint-disable-next-line no-underscore-dangle
  return window.__APP_CONFIG__ ?? {};
}

/**
 * Resolves the API server host. Runtime config wins; falls back to the
 * build-time env so `npm run dev` and tests keep working unchanged.
 */
export function resolveApiServer(config: AppConfig, env: ImportMetaEnv): string {
  return config.API_SERVER ?? (env.PROD ? env.VITE_PROD_API_SERVER_IP : env.VITE_LOCAL_API_SERVER_IP);
}

/** Resolves the login web-server host with the same runtime-wins precedence. */
export function resolveWebServer(config: AppConfig, env: ImportMetaEnv): string | undefined {
  return config.WEB_SERVER ?? env.VITE_WEB_SERVER;
}
